import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, CATEGORY_LABELS, resolveImg } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PencilSimple, Trash, Plus, SignOut, Storefront, EnvelopeSimple, UploadSimple, TextAa, Buildings, CalendarCheck, Images } from "@phosphor-icons/react";
import { UnidadesManager, AgendamentosList } from "@/pages/AdminUnidades";
import { MediaManager } from "@/pages/AdminMedia";

const emptyStore = { name: "", category: "anchor", description: "", image_url: "", order: 0, active: true };

const CONTENT_FIELDS = [
  { key: "hero_subtitle", label: "Subtítulo do Hero", rows: 3 },
  { key: "positioning_paragraph", label: "Parágrafo de Posicionamento", rows: 4 },
  { key: "container_text", label: "Texto dos Estandes (Contêineres)", rows: 4 },
  { key: "parking_text", label: "Texto de Estacionamento", rows: 3 },
  { key: "kaven_description", label: "Descrição do Kaven Restaurante", rows: 3 },
];

export default function AdminDashboard() {
  const nav = useNavigate();
  const [stores, setStores] = useState([]);
  const [leads, setLeads] = useState([]);
  const [content, setContent] = useState({});
  const [savingContent, setSavingContent] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyStore);

  const loadAll = async () => {
    try {
      const [s, l, c] = await Promise.all([
        api.get("/admin/stores"),
        api.get("/admin/leads"),
        api.get("/content"),
      ]);
      setStores(s.data);
      setLeads(l.data);
      setContent(c.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("vm_token");
        nav("/admin/login");
      }
    }
  };

  useEffect(() => {
    if (!localStorage.getItem("vm_token")) { nav("/admin/login"); return; }
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openNew = () => { setEditing(null); setForm(emptyStore); setDialogOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ ...s }); setDialogOpen(true); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name) { toast.error("Informe o nome da operação."); return; }
    const payload = { name: form.name, category: form.category, description: form.description, image_url: form.image_url, order: Number(form.order) || 0, active: form.active };
    try {
      if (editing) await api.put(`/admin/stores/${editing.id}`, payload);
      else await api.post("/admin/stores", payload);
      toast.success(editing ? "Loja atualizada." : "Loja criada.");
      setDialogOpen(false);
      loadAll();
    } catch { toast.error("Erro ao salvar."); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remover esta operação?")) return;
    try { await api.delete(`/admin/stores/${id}`); toast.success("Removida."); loadAll(); }
    catch { toast.error("Erro ao remover."); }
  };

  const uploadFile = async (file) => {
    if (!file) return null;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Imagem enviada.");
      return data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha no upload.");
      return null;
    } finally {
      setUploading(false);
    }
  };

  const uploadStoreImage = async (file) => {
    const url = await uploadFile(file);
    if (url) set("image_url", url);
  };

  const uploadHeroImage = async (file) => {
    const url = await uploadFile(file);
    if (url) setContent((c) => ({ ...c, hero_image: url }));
  };

  const saveContent = async () => {
    setSavingContent(true);
    try {
      const { data } = await api.put("/admin/content", content);
      setContent(data);
      toast.success("Textos atualizados no site.");
    } catch { toast.error("Erro ao salvar textos."); }
    finally { setSavingContent(false); }
  };

  const logout = () => { localStorage.removeItem("vm_token"); nav("/admin/login"); };

  const inp = "w-full bg-vm-muted border border-vm-border rounded-lg px-3 py-2.5 text-sm outline-none focus:border-vm-gold/50 text-vm-fg";

  return (
    <div className="min-h-screen bg-vm-bg text-vm-fg" data-testid="admin-dashboard">
      <header className="border-b border-vm-border sticky top-0 bg-vm-bg/90 backdrop-blur-xl z-40">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div>
            <span className="font-serif text-lg">VM Piabetá</span>
            <span className="text-xs text-vm-mutedFg ml-2">Painel Administrativo</span>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" className="text-sm text-vm-mutedFg hover:text-vm-gold">Ver site</a>
            <button onClick={logout} className="inline-flex items-center gap-2 text-sm border border-vm-border rounded-full px-4 py-2 hover:border-vm-gold/50" data-testid="admin-logout">
              <SignOut size={16} /> Sair
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <Tabs defaultValue="stores">
          <TabsList className="bg-vm-muted border border-vm-border rounded-full p-1 mb-8">
            <TabsTrigger value="stores" className="rounded-full px-5 py-2 data-[state=active]:bg-vm-gold data-[state=active]:text-vm-bg text-vm-mutedFg" data-testid="tab-stores">
              <Storefront size={16} className="mr-2" /> Lojas ({stores.length})
            </TabsTrigger>
            <TabsTrigger value="unidades" className="rounded-full px-5 py-2 data-[state=active]:bg-vm-gold data-[state=active]:text-vm-bg text-vm-mutedFg" data-testid="tab-unidades">
              <Buildings size={16} className="mr-2" /> Unidades
            </TabsTrigger>
            <TabsTrigger value="visitas" className="rounded-full px-5 py-2 data-[state=active]:bg-vm-gold data-[state=active]:text-vm-bg text-vm-mutedFg" data-testid="tab-visitas">
              <CalendarCheck size={16} className="mr-2" /> Visitas
            </TabsTrigger>
            <TabsTrigger value="leads" className="rounded-full px-5 py-2 data-[state=active]:bg-vm-gold data-[state=active]:text-vm-bg text-vm-mutedFg" data-testid="tab-leads">
              <EnvelopeSimple size={16} className="mr-2" /> Propostas ({leads.length})
            </TabsTrigger>
            <TabsTrigger value="content" className="rounded-full px-5 py-2 data-[state=active]:bg-vm-gold data-[state=active]:text-vm-bg text-vm-mutedFg" data-testid="tab-content">
              <TextAa size={16} className="mr-2" /> Textos do Site
            </TabsTrigger>
            <TabsTrigger value="media" className="rounded-full px-5 py-2 data-[state=active]:bg-vm-gold data-[state=active]:text-vm-bg text-vm-mutedFg" data-testid="tab-media">
              <Images size={16} className="mr-2" /> Mídias & Conteúdo
            </TabsTrigger>
          </TabsList>

          <TabsContent value="stores">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-serif text-2xl">Diretório de Operações</h2>
              <button onClick={openNew} className="inline-flex items-center gap-2 bg-vm-gold text-vm-bg font-medium rounded-full px-5 py-2.5 text-sm" data-testid="add-store-btn">
                <Plus size={18} weight="bold" /> Nova Operação
              </button>
            </div>
            <div className="grid gap-3">
              {stores.map((s) => (
                <div key={s.id} className="flex items-center gap-4 bg-vm-surface border border-vm-border rounded-xl p-3" data-testid={`admin-store-${s.id}`}>
                  <img src={resolveImg(s.image_url)} alt={s.name} className="h-14 w-14 rounded-lg object-cover bg-vm-muted" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium truncate">{s.name}</p>
                      {!s.active && <span className="text-[10px] px-2 py-0.5 rounded-full bg-vm-muted text-vm-mutedFg">inativa</span>}
                    </div>
                    <p className="text-xs text-vm-gold">{CATEGORY_LABELS[s.category]}</p>
                    <p className="text-xs text-vm-mutedFg truncate">{s.description}</p>
                  </div>
                  <button onClick={() => openEdit(s)} className="p-2 rounded-lg hover:bg-vm-muted text-vm-mutedFg hover:text-vm-fg" data-testid={`edit-store-${s.id}`}><PencilSimple size={18} /></button>
                  <button onClick={() => remove(s.id)} className="p-2 rounded-lg hover:bg-vm-muted text-vm-mutedFg hover:text-red-400" data-testid={`delete-store-${s.id}`}><Trash size={18} /></button>
                </div>
              ))}
              {!stores.length && <p className="text-vm-mutedFg">Nenhuma operação cadastrada.</p>}
            </div>
          </TabsContent>

          <TabsContent value="leads">
            <h2 className="font-serif text-2xl mb-6">Propostas de Locação</h2>
            <div className="grid gap-3">
              {leads.map((l) => (
                <div key={l.id} className="bg-vm-surface border border-vm-border rounded-xl p-5" data-testid={`admin-lead-${l.id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                    <p className="font-medium">{l.name}</p>
                    <span className="text-xs px-3 py-1 rounded-full bg-vm-gold/10 text-vm-gold">{l.segment}</span>
                  </div>
                  <div className="text-sm text-vm-mutedFg flex flex-wrap gap-x-6 gap-y-1">
                    <span>✉ {l.email}</span>
                    <span>📱 {l.whatsapp}</span>
                    <span>{new Date(l.created_at).toLocaleString("pt-BR")}</span>
                  </div>
                  {l.message && <p className="text-sm mt-3 text-vm-fg/80">{l.message}</p>}
                </div>
              ))}
              {!leads.length && <p className="text-vm-mutedFg">Nenhuma proposta recebida ainda.</p>}
            </div>
          </TabsContent>

          <TabsContent value="content">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-serif text-2xl">Textos Principais do Site</h2>
              <button onClick={saveContent} disabled={savingContent} className="bg-vm-gold text-vm-bg font-medium rounded-full px-5 py-2.5 text-sm disabled:opacity-60" data-testid="save-content-btn">
                {savingContent ? "Salvando..." : "Salvar Textos"}
              </button>
            </div>
            <div className="grid gap-6 max-w-3xl">
              <div className="rounded-xl border border-vm-border bg-vm-surface p-5" data-testid="hero-image-editor">
                <label className="text-xs uppercase tracking-widest text-vm-mutedFg block mb-3">Foto de Fundo do Topo (Hero)</label>
                <div className="flex items-center gap-4">
                  {content.hero_image ? (
                    <img src={resolveImg(content.hero_image)} alt="hero preview" className="h-20 w-32 rounded-lg object-cover bg-vm-muted border border-vm-border" data-testid="hero-image-preview" />
                  ) : (
                    <div className="h-20 w-32 rounded-lg bg-vm-muted border border-vm-border flex items-center justify-center text-vm-mutedFg text-xs">sem foto</div>
                  )}
                  <label className="inline-flex items-center gap-2 cursor-pointer border border-vm-border rounded-full px-4 py-2.5 text-sm hover:border-vm-gold/50 transition-colors">
                    <UploadSimple size={16} /> {uploading ? "Enviando..." : "Enviar foto real do shopping"}
                    <input type="file" accept="image/*" className="hidden" disabled={uploading} onChange={(e) => uploadHeroImage(e.target.files?.[0])} data-testid="hero-image-upload" />
                  </label>
                </div>
                <input className={`${inp} mt-3`} value={content.hero_image || ""} onChange={(e) => setContent((c) => ({ ...c, hero_image: e.target.value }))} placeholder="ou cole uma URL de imagem" data-testid="hero-image-url" />
                <p className="text-xs text-vm-mutedFg mt-2">Clique em “Salvar Textos” para aplicar a nova foto no site.</p>
              </div>
              {CONTENT_FIELDS.map((f) => (
                <div key={f.key}>
                  <label className="text-xs uppercase tracking-widest text-vm-mutedFg">{f.label}</label>
                  <textarea
                    rows={f.rows}
                    className={`${inp} resize-none mt-1.5`}
                    value={content[f.key] || ""}
                    onChange={(e) => setContent((c) => ({ ...c, [f.key]: e.target.value }))}
                    data-testid={`content-${f.key}`}
                  />
                </div>
              ))}
            </div>
          </TabsContent>
          <TabsContent value="media">
            <MediaManager />
          </TabsContent>

          <TabsContent value="unidades">
            <UnidadesManager />
          </TabsContent>

          <TabsContent value="visitas">
            <AgendamentosList />
          </TabsContent>
        </Tabs>
      </main>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-vm-surface border-vm-border text-vm-fg max-w-lg">
          <DialogHeader><DialogTitle className="font-serif text-xl">{editing ? "Editar Operação" : "Nova Operação"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-xs uppercase tracking-widest text-vm-mutedFg">Nome</label>
              <input className={inp} value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="store-form-name" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs uppercase tracking-widest text-vm-mutedFg block mb-1.5">Categoria</label>
                <Select value={form.category} onValueChange={(v) => set("category", v)}>
                  <SelectTrigger className={inp} data-testid="store-form-category"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-vm-muted border-vm-border text-vm-fg">
                    {Object.entries(CATEGORY_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-vm-mutedFg">Ordem</label>
                <input type="number" className={inp} value={form.order} onChange={(e) => set("order", e.target.value)} data-testid="store-form-order" />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-vm-mutedFg block mb-2">Imagem da Loja</label>
              <div className="flex items-center gap-4">
                {form.image_url ? (
                  <img src={resolveImg(form.image_url)} alt="preview" className="h-16 w-16 rounded-lg object-cover bg-vm-muted border border-vm-border" data-testid="store-form-preview" />
                ) : (
                  <div className="h-16 w-16 rounded-lg bg-vm-muted border border-vm-border flex items-center justify-center text-vm-mutedFg text-xs">sem foto</div>
                )}
                <label className="inline-flex items-center gap-2 cursor-pointer border border-vm-border rounded-full px-4 py-2.5 text-sm hover:border-vm-gold/50 transition-colors">
                  <UploadSimple size={16} /> {uploading ? "Enviando..." : "Enviar foto"}
                  <input type="file" accept="image/*" className="hidden" disabled={uploading} onChange={(e) => uploadStoreImage(e.target.files?.[0])} data-testid="store-form-upload" />
                </label>
              </div>
              <input className={`${inp} mt-3`} value={form.image_url} onChange={(e) => set("image_url", e.target.value)} placeholder="ou cole uma URL de imagem" data-testid="store-form-image" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-vm-mutedFg">Descrição</label>
              <textarea rows={3} className={`${inp} resize-none`} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="store-form-description" />
            </div>
            <label className="flex items-center gap-2 text-sm text-vm-mutedFg">
              <input type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)} data-testid="store-form-active" /> Ativa (visível no site)
            </label>
          </div>
          <DialogFooter>
            <button onClick={save} className="bg-vm-gold text-vm-bg font-semibold rounded-full px-6 py-2.5 text-sm w-full" data-testid="store-form-save">Salvar</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
